import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '../i18n';

/** Non-default languages (computed once at module level) */
const NON_DEFAULT_LANGS = SUPPORTED_LANGS.filter((l) => !l.isDefault);

/** Returns the lang object whose URL prefix matches the pathname, or null */
function getLangFromPath(pathname) {
  return NON_DEFAULT_LANGS.find(
    (l) => pathname.startsWith(`/${l.code}/`) || pathname === `/${l.code}`
  ) || null;
}

/**
 * LanguageWrapper - syncs URL language prefix with i18next.
 *
 * URL scheme (driven by SUPPORTED_LANGS in i18n/index.js):
 *   /          → default language (no prefix)
 *   /en/       → English
 *   /en/contact → English contact page
 *   /contact   → default-language contact page
 *
 * Browser-language auto-detection:
 *   On first visit (no localStorage preference saved), if the browser
 *   language matches a supported non-default language, the user is
 *   automatically redirected to the corresponding prefixed URL.
 *   Once a choice is saved in localStorage (via LanguageSwitcher),
 *   this redirect never fires again.
 */
export default function LanguageWrapper({ children }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const didAutoRedirect = useRef(false);

  // ── Effect 1: browser-language auto-redirect (first visit only) ──
  const changeLanguage = useCallback((lng) => i18n.changeLanguage(lng), [i18n]);

  useEffect(() => {
    if (didAutoRedirect.current) return;
    didAutoRedirect.current = true;

    // Only act when the URL is on the default-language tree (no prefix)
    const pathLang = getLangFromPath(location.pathname);
    if (pathLang) return; // already on a prefixed path, nothing to do

    // Check saved preference (localStorage) OR detect browser language
    const savedLang = localStorage.getItem('i18nextLng');
    const browserLangCode = (navigator.language || navigator.userLanguage || '')
      .toLowerCase()
      .split('-')[0];
    const preferredCode = savedLang || browserLangCode;

    // Find a supported non-default language matching the preference
    const matchedLang = NON_DEFAULT_LANGS.find((l) => l.code === preferredCode);
    if (matchedLang) {
      const bare = location.pathname === '/' ? '' : location.pathname;
      const prefixedPath = `/${matchedLang.code}${bare}${location.search}${location.hash}`;
      changeLanguage(matchedLang.code);
      navigate(prefixedPath, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, changeLanguage, navigate]);

  // ── Effect 2: keep i18next in sync with URL prefix on every navigation ──
  useEffect(() => {
    const pathLang = getLangFromPath(location.pathname);
    const targetLang = pathLang ? pathLang.code : DEFAULT_LANG.code;
    if (i18n.language !== targetLang) {
      changeLanguage(targetLang);
    }
  }, [location.pathname, i18n.language, changeLanguage]);

  // ── Effect 3: update <html lang> attribute ──
  useEffect(() => {
    document.documentElement.lang = i18n.language || DEFAULT_LANG.code;
  }, [i18n.language]);

  return children;
}
